import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Grid, Spin, Typography} from 'antd';
import InfiniteScroll from 'react-infinite-scroll-component';
import NewsItem from './NewsItemCard';
import {fromUnifiedCard, newsArticleToUnifiedCard, UnifiedCard} from '../../types/cardData';
import NewsService from '../../service/newsService';
import UserPreferencesService from '../../service/userPreferencesService';
import TagGroupService from '../../service/tagGroupService';
import {ApiResponse, NewsArticlePaginatedResponse, PaginatedResponse} from '../../types/news';
import {useAuth} from '../../contexts/AuthContext';
import {cacheManager, TTL_PRESETS} from '../../utils/cacheManager';

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface PaginationState {
    current: number;
    pageSize: number;
    total: number;
    hasMore: boolean;
}

interface NewsListProps {
    useTrendingNews?: boolean;
    useBreakingNews?: boolean;
    usePersonalizedNews?: boolean;
    searchQuery?: string;
    groupId?: number;
    region?: string;
    relevance?: string;
}

const NewsListComponent: React.FC<NewsListProps> = ({ useTrendingNews = false, useBreakingNews = false, usePersonalizedNews = false, searchQuery, groupId, region, relevance }) => {
    const { token } = useAuth();
    const screens = useBreakpoint();
    const [articles, setArticles] = useState<UnifiedCard[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [pagination, setPagination] = useState<PaginationState>({
        current: 1,
        pageSize: 10,
        total: 0,
        hasMore: true,
    });

    // State preservation - use a more specific key that doesn't change with pathname
    const pageContext = useBreakingNews ? 'breaking' : useTrendingNews ? 'trending' : usePersonalizedNews ? (groupId ? `personalized_${groupId}` : 'personalized') : 'normal';
    const filterKey = `${region || 'all'}_${relevance || 'all'}`;
    const stateKey = `newsList_${pageContext}_${searchQuery || 'no-search'}_${filterKey}`;
    const hasRestoredState = useRef(false);
    const isRestoringScroll = useRef(false);
    const lastScrollTime = useRef(0);
    const currentPageContext = useRef(pageContext);
    const currentFilterKey = useRef(filterKey);
    const currentScrollPosition = useRef({ x: 0, y: 0 });
    const inFlightFetch = useRef<string | null>(null);
    const fetchNewsRef = useRef<(page: number) => Promise<void>>(null as any);

    // Clean up on unmount
    useEffect(() => {
        return () => {
            inFlightFetch.current = null; // Clear in-flight fetch on unmount
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchNews = useCallback(async (page: number): Promise<void> => {
        // Create a unique key for this fetch to prevent duplicates
        const fetchKey = `${pageContext}_${groupId || 'none'}_${filterKey}_page${page}`;

        // Skip if already fetching this exact request
        if (inFlightFetch.current === fetchKey) {
            return;
        }
        inFlightFetch.current = fetchKey;
        setLoading(true);
        setError(null);

        try {
            let newsData: PaginatedResponse<UnifiedCard>;

            if (usePersonalizedNews) {
                // Personalized news still uses V1 — convert to UnifiedCard
                if (!token) {
                    throw new Error('Authentication required for personalized news');
                }
                let v1Response: ApiResponse<NewsArticlePaginatedResponse>;
                if (groupId) {
                    v1Response = await TagGroupService.getPersonalizedNewsByGroup(groupId, page, pagination.pageSize);
                } else {
                    v1Response = await UserPreferencesService.getPersonalizedNews(page, pagination.pageSize);
                }
                if (!v1Response || !v1Response.data) {
                    throw new Error('Invalid response from server');
                }
                const raw = v1Response.data;
                newsData = {
                    data: (raw.data || []).map(newsArticleToUnifiedCard),
                    total: raw.total,
                    page: raw.page,
                    pageSize: raw.pageSize,
                    totalPages: raw.totalPages,
                };
            } else {
                // Use V2 endpoints for all other routes
                const apiFilters = (region || relevance) ? {region, relevance} : undefined;
                let response: ApiResponse<PaginatedResponse<UnifiedCard>>;
                if (searchQuery) {
                    response = await NewsService.searchNewsV2(searchQuery, page, pagination.pageSize);
                } else if (useBreakingNews) {
                    response = await NewsService.getBreakingNewsPaginatedV2(page, pagination.pageSize, apiFilters);
                } else if (useTrendingNews) {
                    response = await NewsService.getTrendingNewsV2(page, pagination.pageSize, apiFilters);
                } else {
                    response = await NewsService.getNewsV2(page, pagination.pageSize, apiFilters);
                }
                if (!response || !response.data) {
                    throw new Error('Invalid response from server');
                }
                newsData = response.data;
            }

            if (!newsData.data || !Array.isArray(newsData.data)) {
                throw new Error('Invalid data format from server');
            }

            setArticles((prev) => page === 1 ? newsData.data : [...prev, ...newsData.data]);
            setPagination({
                current: newsData.page || page,
                pageSize: newsData.pageSize || pagination.pageSize,
                total: newsData.total || 0,
                hasMore: (newsData.page || page) < (newsData.totalPages || 1),
            });
            setError(null);
        } catch (err: any) {
            const errorMessage = err?.message || err?.errorContext?.message || 'Failed to load news articles. Please try again.';
            setError(errorMessage);

            // If it's the first page and we have no articles, show empty state
            if (page === 1) {
                setArticles([]);
            }
        } finally {
            setLoading(false);
            // Clear in-flight fetch marker
            if (inFlightFetch.current === fetchKey) {
                inFlightFetch.current = null;
            }
        }
    }, [pagination.pageSize, useBreakingNews, useTrendingNews, usePersonalizedNews, searchQuery, token, groupId, pageContext, region, relevance, filterKey]);

    fetchNewsRef.current = fetchNews;

    // Handle groupId changes for personalized news
    useEffect(() => {
        const shouldHandleGroupChange = usePersonalizedNews && groupId !== undefined;
        if (!shouldHandleGroupChange) {
            return;
        }

        // Check if we have cached state for this group
        const cachedState = cacheManager.get<any>(stateKey);

        // If we have cached state, let the restoration effect handle it
        if (cachedState && cachedState.articles && cachedState.articles.length > 0) {
            return; // Let restoration effect handle it
        }

        // No cached state - clear and fetch fresh data
        inFlightFetch.current = null;
        setArticles([]);
        setPagination({
            current: 1,
            pageSize: 10,
            total: 0,
            hasMore: true,
        });
        setError(null);
        hasRestoredState.current = false;
        currentPageContext.current = pageContext;
        fetchNewsRef.current(1);
    }, [groupId, usePersonalizedNews, pageContext, stateKey]);

    // Restore state on mount - only for the same page context
    useEffect(() => {
        // Reset restoration flag when stateKey changes (new page context)
        const isContextChanged = currentPageContext.current !== pageContext;
        if (isContextChanged) {
            hasRestoredState.current = false;
        }

        // Use cacheManager instead of direct sessionStorage
        const savedState = cacheManager.get<any>(stateKey);

        if (savedState && !hasRestoredState.current && !isContextChanged) {
            // Cache manager already handles TTL check, so if we get data it's fresh
            const isSameContext = savedState.pageContext === pageContext;

            if (isSameContext && savedState.articles && savedState.articles.length > 0) {
                setArticles(savedState.articles || []);
                setPagination(savedState.pagination || {
                    current: 1,
                    pageSize: 10,
                    total: 0,
                    hasMore: true,
                });
                setError(savedState.error || null);
                hasRestoredState.current = true;

                // Restore scroll position
                const targetScrollPosition = savedState.scrollPosition;

                if (targetScrollPosition && (targetScrollPosition.x !== 0 || targetScrollPosition.y !== 0)) {
                    // Check if user has been scrolling recently (within last 1 second)
                    const timeSinceLastScroll = Date.now() - lastScrollTime.current;
                    if (timeSinceLastScroll > 1000) {
                        isRestoringScroll.current = true;

                        const restoreScroll = () => {
                            if (isRestoringScroll.current) {
                                window.scrollTo({
                                    left: targetScrollPosition.x,
                                    top: targetScrollPosition.y
                                });
                            }
                        };

                        // Use requestAnimationFrame for optimal scroll restoration
                        const performRestoration = () => {
                            restoreScroll();
                            // Clear restoration flag after restoration
                            requestAnimationFrame(() => {
                                isRestoringScroll.current = false;
                            });
                        };

                        // Try immediate restoration, then backup with requestAnimationFrame
                        restoreScroll();
                        requestAnimationFrame(performRestoration);
                    }
                }

                // Reset restoration flag after initial load to allow future saves
                setTimeout(() => {
                    hasRestoredState.current = false;
                }, 1000);

                return; // Don't fetch new data if we restored state
            } else {
                cacheManager.remove(stateKey);
            }
        }

        // Always fetch fresh data if no valid state was restored or context changed
        if (!hasRestoredState.current || isContextChanged) {
            if (!isContextChanged) {
                fetchNewsRef.current(1);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stateKey, pageContext]);

    // Save state when data changes
    useEffect(() => {
        const saveState = () => {
            const stateToSave = {
                articles,
                pagination,
                error,
                scrollPosition: currentScrollPosition.current, // Use ref instead of window.scrollY
                pageContext, // Include page context to ensure state belongs to the right page
            };

            // Use cacheManager with MEDIUM TTL (5 minutes) for consistency
            cacheManager.set(stateKey, stateToSave, { ttl: TTL_PRESETS.MEDIUM });
        };

        // Always save state when we have articles (removed the hasRestoredState check)
        if (articles.length > 0) {
            saveState();
        }

        // Save state before unmount only if we have meaningful data
        return () => {
            if (articles.length > 0) {
                requestAnimationFrame(() => {
                    saveState();
                });
            }
        };
    }, [articles, pagination, error, stateKey, pageContext, usePersonalizedNews, groupId]);

    // Save scroll position on scroll events (minimal debounce)
    useEffect(() => {
        let timeoutId: NodeJS.Timeout;
        let lastSavedPosition = { x: 0, y: 0 };

        const handleScroll = () => {
            // Update last scroll time
            lastScrollTime.current = Date.now();

            // ALWAYS update the ref immediately (no debounce)
            currentScrollPosition.current = {
                x: window.scrollX,
                y: window.scrollY
            };

            // Don't save scroll position if we're currently restoring
            if (isRestoringScroll.current) {
                return;
            }

            // Debounce the cache persistence (not the ref update)
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                const currentState = cacheManager.get<any>(stateKey);
                if (currentState) {
                    const newScrollPosition = currentScrollPosition.current;

                    // Only save if position changed significantly (more than 10px)
                    if (Math.abs(newScrollPosition.y - lastSavedPosition.y) > 10) {
                        currentState.scrollPosition = newScrollPosition;
                        // Re-save with MEDIUM TTL (5 minutes)
                        cacheManager.set(stateKey, currentState, { ttl: TTL_PRESETS.MEDIUM });
                        lastSavedPosition = newScrollPosition;
                    }
                }
            }, 100); // Reduced debounce to 100ms for faster saves
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
            clearTimeout(timeoutId);

            // Save final position on unmount without debounce, using ref
            const currentState = cacheManager.get<any>(stateKey);
            if (currentState && articles.length > 0) {
                currentState.scrollPosition = currentScrollPosition.current;
                cacheManager.set(stateKey, currentState, { ttl: TTL_PRESETS.MEDIUM });
            }
        };
    }, [stateKey, articles.length, usePersonalizedNews, groupId]);



    // Handle changes in page context (Home, Trending, Personalized), search query, or filters
    useEffect(() => {
        // Check if page context or filters have changed
        const hasPageContextChanged = currentPageContext.current !== pageContext;
        const hasFilterChanged = currentFilterKey.current !== filterKey;

        if (hasPageContextChanged || hasFilterChanged) {
            setArticles([]);
            setPagination({
                current: 1,
                pageSize: 10,
                total: 0,
                hasMore: true,
            });
            setError(null);
            hasRestoredState.current = false;


            if (hasPageContextChanged) {
                const oldStateKey = `newsList_${currentPageContext.current}_${searchQuery || 'no-search'}_${currentFilterKey.current}`;
                cacheManager.remove(oldStateKey);
                currentPageContext.current = pageContext;
            }
            currentFilterKey.current = filterKey;

            fetchNews(1);
        } else if (searchQuery !== undefined) {
            setArticles([]);
            setPagination({
                current: 1,
                pageSize: 10,
                total: 0,
                hasMore: true,
            });
            setError(null);
            hasRestoredState.current = false;


            cacheManager.remove(stateKey);

            fetchNews(1);
        }
    }, [pageContext, searchQuery, fetchNews, stateKey, usePersonalizedNews, groupId, filterKey]);

    // Fallback: If we have no articles and no loading, try to fetch
    useEffect(() => {
        const shouldDisableCache = usePersonalizedNews && groupId !== undefined;
        if (shouldDisableCache) {
            return;
        }

        if (articles.length === 0 && !loading && !error && !hasRestoredState.current) {
            fetchNewsRef.current(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [articles.length, loading, error, usePersonalizedNews, groupId]);

    const loadMore = (): void => {
        if (pagination.hasMore && !loading) {
            fetchNews(pagination.current + 1);
        }
    };

    return (
        <div
            className="news-list-container"
            style={{
                padding: screens.md ? '24px' : '16px',
                maxWidth: screens.md ? '1000px' : '100%',
                margin: '0 auto'
            }}
        >
            {error && (
                <Text type="danger" style={{ display: 'block', marginBottom: '16px' }}>
                    {error}
                </Text>
            )}
            <InfiniteScroll
                dataLength={articles.length}
                next={loadMore}
                hasMore={pagination.hasMore}
                loader={<Spin spinning={loading} style={{ display: 'block', textAlign: 'center', margin: '16px 0' }} />}
                endMessage={
                    articles.length > 0 && !loading ? (
                        <Text style={{ display: 'block', textAlign: 'center', margin: '16px 0' }}>
                            No more articles to load.
                        </Text>
                    ) : null
                }
            >
                {articles.length > 0 ? (
                    articles.map((article, index) => (
                        <NewsItem
                            key={`${article.id}-${index}`}
                            data={fromUnifiedCard(article)}
                            showClusterInfo={true}
                        />
                    ))
                ) : (
                    !loading &&
                    !error && (
                        <Text style={{ display: 'block', textAlign: 'center' }}>
                            {searchQuery
                                ? 'No articles found matching your search.'
                                : useBreakingNews
                                    ? 'No breaking news at the moment.'
                                    : useTrendingNews
                                        ? 'No trending articles available right now.'
                                        : 'No news articles available right now.'}
                        </Text>
                    )
                )}
            </InfiniteScroll>
        </div>
    );
};

// Memoize component to prevent unnecessary re-renders when parent re-renders
const NewsList = React.memo(NewsListComponent);

export default NewsList;